import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import * as qs from 'qs'
import { Link, useNavigate, useSearchParams } from 'react-router'
import classNames from 'classnames'
import moment from 'moment'
import { toast } from 'react-toastify'

// evertel
import { Links } from '@evertel/constants'
import { useService } from '@evertel/di'
import { LAST_ACTIVITY_STORAGE_KEY } from '@evertel/web/core'
import { SessionService } from '@evertel/session/SessionService'
import { LoginService } from '@evertel/session/LoginService'
import { LogoutService, LogoutReason } from '@evertel/session/LogoutService'
import { Analytics } from '@evertel/analytics'
import { InfoBox, Card, CardBody, Col, Row, Button, Icon, useUI, FormInput, PasswordInput, Logo } from '@evertel/web/ui'

const LOGOUT_REASON_MESSAGES: Record<LogoutReason, string> = {
    'manual': '', // No message for manual logout
    'idle_timeout': 'You have been logged out due to inactivity',
    'token_expired': 'Your session has expired. Please log in again',
    'password_changed': 'Your password has been changed. Please log in with your new password',
    'email_changed': 'Your email has been changed. Please log in with your new email',
    'other': 'You have been logged out'
}
const STALE_SESSION_MINUTES = 4 * 60
const TOASTER_ID = 'logout-reason'

const Login = observer(() => {
    const sessionService = useService(SessionService, [])
    const logoutService = useService(LogoutService, [])
    const analytics = useService(Analytics)
    const loginService = useService(LoginService)
    const cookiesEnabled = navigator?.cookieEnabled

    const [searchParams] = useSearchParams()

    const emailParam = searchParams.get('email') || ''
    const autoLogin = !!emailParam

    const [isLoading, setIsLoading] = useState(false)
    const [email, setEmail] = useState(emailParam)
    const [password, setPassword] = useState('')
    const [enterpriseView, setEnterpriseView] = useState(true)
    const [infoMessage, setInfoMessage] = useState({
        color: '',
        text: ''
    })

    const { addToast, showAppLoader } = useUI()

    const navigate = useNavigate()

    useEffect(() => {
        if (sessionService.currentUserId) {
            //user is already logged in?  go to landing?
            navigate('/landing', { replace: true })
        }
    }, [sessionService.currentUserId])

    useEffect(() => {
        analytics.logView('login')
    }, [])

    // Check for stored logout reason and show toast
    useEffect(() => {
        const checkLogoutReason = async () => {
            const reason = await logoutService.getStoredLogoutReason()
            if (!reason || !LOGOUT_REASON_MESSAGES[reason]) return

            const message = LOGOUT_REASON_MESSAGES[reason]
            if (!message) return

            // For token expiration, only show message if user was recently active
            // (avoids confusing "session expired" when returning after a long absence)
            if (reason === 'token_expired') {
                const lastActivityStr = await localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)
                if (lastActivityStr) {
                    const lastActivity = moment(lastActivityStr)
                    const minutesSinceActivity = moment().diff(lastActivity, 'minutes', true)
                    if (minutesSinceActivity > STALE_SESSION_MINUTES) {
                        logoutService.clearStoredLogoutReason()
                        return
                    }
                }
            }

            addToast({
                message,
                color: 'warning',
                autoClose: false,
                toastId: TOASTER_ID
            })

            // if this page is refreshed too fast, don't clear it yet
            setTimeout(() => {
                logoutService.clearStoredLogoutReason()
            }, 3000)
        }
        checkLogoutReason()
    }, [])

    useEffect(() => {
        // grab any message from the query string and set it to state.
        // used primarily for Auth0 redirects.
        const queryString = qs.parse(window?.location?.search?.replace('?', ''))

        if (queryString?.message) {
            setInfoMessage({
                color: (queryString.success === 'true') ? 'success' : 'warning',
                text: queryString.message + ' Please login to continue.'
            })
        }
    }, [])

    const handleEmailVerificationNeeded = (email: string, password?: string) => {
        // Navigate to email verification screen and Pass credentials for auto-login after verification
        loginService.setTempCredentials(email, password)
        navigate('/email-verification')
    }

    const handleMFANeeded = () => {
        // Navigate to 2FA screen
        navigate('/two-factor')
    }

    const handleSSORedirect = (redirectUrl: string) => {
        // Navigate to external SSO URL
        if (!redirectUrl) return

        // if no http or https add it
        if (!/^https?:\/\//i.test(redirectUrl)) {
            redirectUrl = 'http://' + redirectUrl
        }

        window.open(redirectUrl, '_self')
    }

    const handleEnterpriseError = (error: any) => {
        // Enterprise login failed, show password field
        if (error.code === 'ENTERPRISE_NOT_FOUND' || error.status === 404) {
            setEnterpriseView(false)
        } else {
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message
            })
        }
    }

    const handleAutoJoinFailed = () => {
        // Navigate to department access code screen
        // Timeout so this happens after the default navigation triggers from currentUserId in App.tsx
        setTimeout(() => {
            navigate('/account/departments/join')
        }, 1500)
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value)

    }
    const handlePasswordChange = (password) => {
        setPassword(password)
    }

    const doLogin = async (e?: React.FormEvent<HTMLFormElement>) => {
        if (e) {
            e.preventDefault()
        }


        const emailTrim = email.trim()
        const passwordTrim = password.trim()

        // validate
        if (!emailTrim || (!enterpriseView && !passwordTrim)) {
            setInfoMessage({
                text: 'Please complete all required fields',
                color: 'danger'
            })
            return
        }


        showAppLoader({isOpen: true, type: 'default'}) //'page-loading'

        setIsLoading(true)

        try {
            if (enterpriseView) {
                try {
                    const result = await loginService.checkEnterprise(emailTrim)
                    handleSSORedirect(result.redirectUrl)

                } catch (enterpriseError: any) {
                    handleEnterpriseError(enterpriseError)
                }

                showAppLoader({isOpen: false, type: 'default'}) //'page-loading'
                return

            } else {
                // Login with password or accessTokenSSO
                try {
                    const result = await loginService.loginWithPassword(emailTrim, passwordTrim)

                    if ('redirectUrl' in result) {
                        // SSO redirect fallback
                        handleSSORedirect(result.redirectUrl)
                    } else if ('mfaRequired' in result) {
                        // MFA needed
                        handleMFANeeded()
                    } else {
                        // Login success automatically managed
                    }

                } catch ( loginError: any) {
                    if (loginError.message?.includes('Please verify your email') ||
                        loginError.code === 'EMAIL_NOT_VERIFIED') {
                        handleEmailVerificationNeeded(emailTrim, passwordTrim)
                        return
                    }

                    if (loginError.code === 'AUTO_JOIN_FAILED') {
                        handleAutoJoinFailed()
                        return
                    }

                    addToast({
                        title: 'Error',
                        color: 'danger',
                        message: loginError.message || loginError
                    })
                }
            }

        } catch (error: any) {
            console.log(error)
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message || error
            })
        } finally {
            setIsLoading(false)
            toast.dismiss(TOASTER_ID)
        }

        showAppLoader({isOpen: false, type: 'default'})
    }

    useEffect(() => {
        if (autoLogin) {
            doLogin()
        }
    }, [])

    return (
        <Row
            key="evertelLoginScreen"
            className="login auth-layout animated"
        >
            <Col valign="center" align="center"
                className="p-md-5 p-3"
                style={{ minHeight: '100vh', margin: 'auto', overflow: 'auto', maxWidth: 900 }}
            >
                {/* LOGIN BLOCK */}
                <Card className="p-4 fadeIn animated login-card">
                    <CardBody>
                        <Row className="flex-wrap">
                            <Col xs={12} md={6}>
                                <Logo className="mb-4" />
                                <h1 className="fs-2">
                                    Sign in to your account
                                </h1>
                                <label className="mb-2 mt-1 fw-bold" htmlFor="email">
                                    Email
                                </label>
                                {(!!infoMessage?.text) &&
                                <InfoBox color={infoMessage.color}>
                                    {infoMessage.text}
                                </InfoBox>
                                }
                                {(cookiesEnabled) &&
                                <form onSubmit={doLogin}>
                                    <FormInput
                                        id="email"
                                        name="email"
                                        value={email}
                                        placeholder="Enter your email"
                                        onChange={handleEmailChange}
                                        leftIcon="at"
                                        className="mb-4"
                                    />
                                    <div
                                        className={classNames('mb-4', {
                                            'd-none invisible':
                                                enterpriseView
                                        })}
                                    >
                                        <PasswordInput
                                            label="Password"
                                            placeholder="Please enter your password"
                                            value={password}
                                            onChange={handlePasswordChange}
                                            required={false} //just to hide the star
                                        />
                                    </div>
                                    <Row>
                                        <Col>
                                            <Button
                                                type="submit"
                                                style={{ minWidth: 100 }}
                                                color="primary"
                                                loading={isLoading}
                                                disabled={isLoading}
                                                aria-busy={isLoading}
                                            >
                                                {(enterpriseView) ? 'Continue' : 'Login'}
                                            </Button>
                                        </Col>
                                        {(enterpriseView) &&
                                            <Col xs={6} />
                                        }
                                        {(!enterpriseView) &&
                                            <Col
                                                className="ps-3 text-right"
                                                align="right"
                                                valign="center">
                                                <Link
                                                    className="btn btn-link text-nowrap"
                                                    to="/login/forgotpassword"
                                                    state={{ email }}
                                                >Forgot password?</Link>
                                            </Col>
                                        }
                                    </Row>
                                    <Row className="mt-5">
                                        <strong>
                                            Don't have an account?
                                        </strong>
                                        <Link
                                            className="btn btn-link mx-2 text-nowrap"
                                            to="/register"
                                        >
                                            Create Account
                                        </Link>
                                    </Row>
                                </form>
                                }
                                {(!cookiesEnabled) &&
                                <InfoBox color="danger">
                                    <strong>Cookies are disabled: </strong><br />
                                    In order to use Evertel, you will need to enable cookies in your browser settings.<br />
                                    <Button
                                        color="link"
                                        onClick={() => navigate(Links.cookiesHelp)}>
                                        Learn how to enable cookies
                                    </Button>
                                </InfoBox>
                                }
                            </Col>
                            <Col xs={12} md={6} className="justify-content-center ps-md-5  mt-4 mt-md-0">
                                <Card className="crisis-splash-card">
                                    <CardBody>
                                        <p className="px-2 fw-bold">Your agency isn't registered?</p>
                                        <p className="px-2 mb-5">
                                            If you're responding to a crisis event and your agency is not registered on Evertel
                                            you can still collaborate with the agencies responding.
                                        </p>
                                        <Button
                                            color="transparent"
                                            className="mt-5 fw-bold w-100 crisis-button"
                                            onClick={() => navigate('/login/crisissplash')}>
                                            <Icon
                                                name="fire"
                                                type="solid"
                                                color="danger"
                                                className="me-2"
                                            />
                                            Responding to a Crisis Event?
                                        </Button>
                                    </CardBody>
                                </Card>
                                {/* <Col align='start' valign='center'>
                                </Col> */}
                            </Col>
                        </Row>
                        <div className="mt-4">
                            <small className="fs-6">
                                You are accessing a restricted information system. Do not login to Evertel on public computers or on unsecured networks.
                                System usage may be monitored, recorded, and subject to audit. Unauthorized use of the system is prohibited and may be subject to criminal and/or civil
                                penalties. Use of the system indicates consent to monitoring and recording.
                                |
                            </small>
                            <a href={Links.helpCenter} className="fs-5" target="_blank" rel="noopener noreferrer">
                                <small> Help Center </small>
                            </a>
                            <small>|</small>
                            <a href="mailto:EvertelSupport@genasys.com" className="fs-5" rel="noopener noreferrer">
                                <small> Contact us </small>
                            </a>
                        </div>
                    </CardBody>
                </Card>
                <div>
                    <div className="mt-2">
                    </div>
                    {!(import.meta.env.PROD) && // for debugging
                        <div className="mt-4">
                            <small>
                                Version: <strong>{__BUILD__.VERSION}</strong>,
                                Build Number: <strong>{__BUILD__.NUMBER}</strong>,
                                Commit: <strong>{__BUILD__.COMMIT}</strong>,
                                Branch: <strong>{__BUILD__.BRANCH}</strong>,
                                BuildDate: <strong>{__BUILD__.DATE}</strong>,
                                Mode: <strong>{import.meta.env.MODE}</strong>,
                                Production: <strong>{import.meta.env.PROD ? 'YES' : 'NO'}</strong>
                            </small>
                        </div>
                    }
                </div>
            </Col>
        </Row>
    )
})

export { Login }
